import { readFileSync, existsSync } from 'fs';
import type { FrameworkIntegration, DetectionResult, IntegrationConfig } from './types.js';

interface PackageJson {
  dependencies?: Record<string, string>;
  devDependencies?: Record<string, string>;
}

export const langchainIntegration: FrameworkIntegration = {
  name: 'langchain',
  description: 'LangChain framework',

  detect(): Promise<DetectionResult> {
    let hasLangchain = false;

    if (existsSync('package.json')) {
      try {
        const pkg = JSON.parse(readFileSync('package.json', 'utf-8')) as PackageJson;
        const deps: Record<string, string> = { ...pkg.dependencies, ...pkg.devDependencies };
        hasLangchain = 'langchain' in deps || '@langchain/core' in deps;
      } catch {
        // Ignore
      }
    }

    return Promise.resolve({
      detected: hasLangchain,
      confidence: hasLangchain ? 0.9 : 0,
      files: hasLangchain ? ['package.json'] : [],
    });
  },

  generate(config: IntegrationConfig): Promise<string> {
    return Promise.resolve(`---
name: mutagent-langchain-integration
description: Integrate MutagenT with LangChain for prompt optimization and trace observability
framework: langchain
version: 1.0.0
---

# MutagenT + LangChain Integration

## Prerequisites Verification

- MUTAGENT_API_KEY: ${config.apiKey.slice(0, 8)}...${config.apiKey.slice(-4)}
- MUTAGENT_ENDPOINT: ${config.endpoint}
- API Connection: Verified

## Installation

\`\`\`bash
# The @mutagent/langchain package was installed automatically
# If you need to install manually:
bun add @mutagent/langchain
# or
npm install @mutagent/langchain
\`\`\`

## Configuration

### 1. Environment Variables

\`\`\`env
MUTAGENT_API_KEY=${config.apiKey}
MUTAGENT_ENDPOINT=${config.endpoint}
\`\`\`

### 2. LangChain Callback Handler

\`\`\`typescript
import { ChatOpenAI } from '@langchain/openai';
import { MutagentCallbackHandler } from '@mutagent/langchain';
import { initTracing } from '@mutagent/sdk/tracing';

// Initialize tracing (or set MUTAGENT_API_KEY env var for auto-init)
initTracing({
  apiKey: process.env.MUTAGENT_API_KEY!,
  endpoint: process.env.MUTAGENT_ENDPOINT,
});

// Create callback handler (no args needed — uses SDK tracing)
const handler = new MutagentCallbackHandler();

const llm = new ChatOpenAI({
  callbacks: [handler],
});

const result = await llm.invoke('Hello world');
// Traces automatically captured via LangChain callbacks
\`\`\`

### 3. Chain Integration

\`\`\`typescript
import { ChatOpenAI } from '@langchain/openai';
import { PromptTemplate } from '@langchain/core/prompts';
import { MutagentCallbackHandler } from '@mutagent/langchain';

const handler = new MutagentCallbackHandler();

const llm = new ChatOpenAI({
  callbacks: [handler],
});

const template = PromptTemplate.fromTemplate('Answer the question: {question}');
const chain = template.pipe(llm);

const result = await chain.invoke(
  { question: 'What is TypeScript?' },
  { callbacks: [handler] },
);
\`\`\`

## Migration from Langfuse

Replace Langfuse callback:

\`\`\`typescript
// Before
import { CallbackHandler } from 'langfuse-langchain';
const callbacks = [new CallbackHandler()];

// After
import { MutagentCallbackHandler } from '@mutagent/langchain';
const callbacks = [new MutagentCallbackHandler()];
\`\`\`

## Verification

\`\`\`bash
mutagent integrate langchain --verify
\`\`\`

## Documentation

- Full guide: https://docs.mutagent.io/integrations/langchain
- API Reference: https://docs.mutagent.io/sdk/tracing
- Dashboard: https://app.mutagent.io

## CLI Reference

\`\`\`bash
# View traces
mutagent traces list --prompt <prompt-id>

# Analyze performance
mutagent traces analyze <prompt-id>
\`\`\`
`);
  },
};
